// frontend/src/components/masters/TransporterForm.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'react-toastify';
import { usePermissions } from '../../services/PermissionsContext'; // 1. Import
import './TransporterForm.css'; // Component-specific styles (if any, or will use global/inline)
import { openFilePreview } from '../common/FilePreviewModal';
import Select2Dropdown from '../common/Select2Dropdown'; // Import Select2Dropdown

const API_BASE_URL = '/api/masters/transporters/'; // Assuming Django API endpoint for Transporters
const BANKS_API_URL = '/api/masters/banks/';

// Helper to get auth token
const getAuthHeaders = (isFormData = false) => {
    const headers = {
        'Authorization': `Bearer ${localStorage.getItem('kk_token')}`
    };
    if (!isFormData) {
        headers['Content-Type'] = 'application/json';
    }
    // For FormData, do not set Content-Type, let the browser do it.
    return headers;
};

const TransporterForm = ({ mode, initialData, onClose }) => {
    // Initial state for the transporter, strictly based on the new requirements
    const [transporter, setTransporter] = useState(initialData || {
        // Transporter Information Section
        name: '',
        phone: '',
        address: 'P.O.BOX ', // Default prefix
        email: '', // Added Email field

        // Registration Details Section
        tin: '',
        tin_document: null, // For TIN Document Upload (File object)
        vrn: '',
        vrn_document: null, // For VRN Document Upload (File object)
        vrn_na: false,

        // Attachments Section (array of objects with type and file)
        attachments: [], // Each item will be { type: '', file: null }
        bank_details: [{ bank: '', swift_code: '', currency: 'TZS', account_number: '' }]
    });

    const [readOnly, setReadOnly] = useState(mode === 'view');
    const [error, setError] = useState(null);
    const [message, setMessage] = useState(null);
    const [banks, setBanks] = useState([]);
    const { hasPermission } = usePermissions(); // 2. Get hook

    // Country and City states are no longer needed for this specific form
    // const [countries, setCountries] = useState([]);
    // const [cities, setCities] = useState([]);

    // Refs for file inputs and preview modal
    // tinDocumentRef and vrnDocumentRef are now managed directly by file input's state
    const attachmentRefs = useRef([]); // To hold refs for multiple attachment inputs

    const previewModalRef = useRef(null);
    const previewIframeRef = useRef(null);
    const previewImageRef = useRef(null);
    const previewDownloadDivRef = useRef(null);
    const [isMaximized, setIsMaximized] = useState(false);

    // Effect to set initial data or default values for new forms
    useEffect(() => {
        if (initialData) {
            // Ensure address has the prefix if it's missing or empty from initialData
            const addressToSet = (!initialData.address || initialData.address === '')
                ? 'P.O.BOX '
                : initialData.address.startsWith('P.O.BOX') 
                    ? initialData.address 
                    : `P.O.BOX ${initialData.address}`;

            // Update initialData structure for attachments if coming from backend
            // Backend might send attachment URLs as strings, so adapt them.
            const initialAttachments = initialData.attachments ? initialData.attachments.map(att => ({
                type: att.attachment_type || '', // Backend uses 'attachment_type'
                file: att.document_file || null, // Backend uses 'document_file' (URL string)
            })) : [];

            console.log('Processing initial attachments:', initialAttachments);


            setTransporter(prev => ({
                ...prev, // Keep default structure for missing initialData fields
                ...initialData, // Override with existing data
                address: addressToSet,
                tin_document: initialData.tin_document || null,
                vrn_document: initialData.vrn_document || null,
                vrn_na: !!initialData.vrn_na,
                attachments: initialAttachments, // Set processed attachments
                bank_details: initialData.bank_details && initialData.bank_details.length > 0 ? initialData.bank_details : [{ bank: '', swift_code: '', currency: 'TZS', account_number: '' }]
            }));
        } else {
            // For new forms, ensure address starts with the prefix
            setTransporter(prev => ({
                ...prev,
                address: 'P.O.BOX '
            }));
        }
        setReadOnly(mode === 'view'); // Set readOnly based on the mode prop
    }, [mode, initialData]); // Removed 'countries' from dependency array

    const loadBanks = useCallback(async () => {
        try {
            const response = await fetch(BANKS_API_URL, { headers: getAuthHeaders() });
            if (response.ok) {
                const data = await response.json();
                setBanks(data.results || data);
            } else {
                console.error("Failed to fetch banks");
            }
        } catch (err) {
            console.error("Error fetching banks:", err);
        }
    }, []);

    useEffect(() => {
        loadBanks();
    }, [loadBanks]);

    // Removed Geolocation Data Loading effects as Country/City are no longer present.
    // const loadCountries = useCallback(async () => { /* ... */ }, []);
    // const updateCities = useCallback(async (countryCode) => { /* ... */ }, []);
    // useEffect(() => { loadCountries(); }, [loadCountries]);
    // useEffect(() => { /* updateCities logic */ }, [transporter.country, updateCities]);

    // Effect to ensure address has P.O.BOX prefix on add mode or if cleared
    useEffect(() => {
        if (mode === 'add' && (!transporter.address || transporter.address === '' || transporter.address === 'P.O.BOX ')) {
            setTransporter(prev => ({ ...prev, address: 'P.O.BOX ' }));
        }
    }, [mode, transporter.address]);


    // --- Form Field Change Handlers ---

    // Handles changes for top-level input fields (name, phone, address, email, tin, vrn, bank, account)
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (readOnly) return;

        if (name === 'vrn_na') {
            setTransporter(prev => ({
                ...prev,
                vrn_na: checked,
                vrn: checked ? '' : prev.vrn // Clear VRN if N/A checked
            }));
            return;
        }

        let newValue = value;
        // Convert to uppercase for specific text fields
        if (['name', 'address', 'tin', 'vrn', 'bank_name', 'account_number'].includes(name)) {
            newValue = value.toUpperCase();
        }

        setTransporter(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : newValue
        }));
    };

    const handleRepeatedChange = (e, index, fieldName, subArrayName) => {
        if (readOnly) return;
        const { value } = e.target;

        if (subArrayName === 'bank_details' && fieldName === 'bank') {
            const selectedBank = banks.find(b => b.id === parseInt(value));
            setTransporter(prev => {
                const updatedArray = [...prev.bank_details];
                updatedArray[index] = { 
                    ...updatedArray[index], 
                    bank: selectedBank ? selectedBank.id : '',
                    swift_code: selectedBank ? selectedBank.swift_code : ''
                };
                return { ...prev, [subArrayName]: updatedArray };
            });
            return;
        }

        setTransporter(prev => {
            const updatedArray = [...prev[subArrayName]];
            updatedArray[index] = { ...updatedArray[index], [fieldName]: value };
            return { ...prev, [subArrayName]: updatedArray };
        });
    }; 
    
    const getDisplayAddress = (address) => {
        // Remove P.O.BOX prefix for display in the input field
        if (!address) return '';
        return address.replace(/^P\.O\.BOX\s*/i, '').trim();
    };

    const getFullAddress = (addressValue) => {
        // Add P.O.BOX prefix when saving
        if (!addressValue) return 'P.O.BOX ';
        const cleanAddress = addressValue.replace(/^P\.O\.BOX\s*/i, '').trim();
        return cleanAddress ? `P.O.BOX ${cleanAddress}` : 'P.O.BOX ';
    };

    const handleAddressChange = (e) => {
        if (readOnly) return;
        const inputValue = e.target.value;
        // Store with P.O.BOX prefix
        const fullAddress = getFullAddress(inputValue);
        setTransporter(prev => ({ ...prev, address: fullAddress }));
    };


    const handleFileChange = (e, fieldName, index = null) => {
        if (readOnly) return;
        const file = e.target.files[0];
        if (!file) return;

        if (fieldName === 'tin_document' || fieldName === 'vrn_document') {
            // Create preview URL for new files
            const previewURL = URL.createObjectURL(file);
            setTransporter(prev => ({ 
                ...prev, 
                [fieldName]: file,
                [`${fieldName}_preview`]: previewURL  // Store preview URL for new files
            }));
        } else if (fieldName === 'attachments' && index !== null) {
            setTransporter(prev => {
                const updatedAttachments = [...prev.attachments];
                updatedAttachments[index] = { 
                    ...updatedAttachments[index], 
                    file_obj: file, 
                    document_file: URL.createObjectURL(file) 
                };
                return { ...prev, attachments: updatedAttachments };
            });
        }
    };

       // --- Repeatable Section Logic ---
    const addRepeatedItem = (subArrayName, newItem) => {
        if (readOnly) return;
        setTransporter(prev => ({ ...prev, [subArrayName]: [...prev[subArrayName], newItem] }));
    };

    const removeRepeatedItem = (subArrayName, index) => {
        if (readOnly) return;
        setTransporter(prev => ({ ...prev, [subArrayName]: prev[subArrayName].filter((_, i) => i !== index) }));
    };

   

    const openPreview = (fileURL, fileName) => {
        openFilePreview(fileURL, fileName, {
            customTitle: `Preview: ${fileName || 'Document'}`,
            onDownload: (data) => {
                // Custom download handler if needed
                console.log('Download requested:', data);
            },
            onClose: () => {
                // Custom close handler if needed
                console.log('Preview window closed');
            }
        });
    };

    const closePreview = () => {
        if (previewModalRef.current && previewIframeRef.current && previewImageRef.current && previewDownloadDivRef.current) {
            previewModalRef.current.style.display = 'none';
            previewIframeRef.current.src = '';
            previewImageRef.current.src = '';
            previewDownloadDivRef.current.style.display = 'none';
            setIsMaximized(false); // Reset maximize state
            Object.assign(previewModalRef.current.style, {
                top: previewModalRef.current.dataset.originalTop || '50px',
                left: previewModalRef.current.dataset.originalLeft || '50px',
                width: previewModalRef.current.dataset.originalWidth || '80%',
                height: previewModalRef.current.dataset.originalHeight || '80%',
                resize: 'both'
            });
        }
    };

    const toggleMaximize = () => {
        if (!previewModalRef.current) return;
        const modal = previewModalRef.current;
        const btn = modal.querySelector('#maximizeBtn');

        if (!isMaximized) {
            modal.dataset.originalLeft = modal.style.left;
            modal.dataset.originalTop = modal.style.top;
            modal.dataset.originalWidth = modal.style.width;
            modal.dataset.originalHeight = modal.style.height;

            modal.style.top = '0';
            modal.style.left = '0';
            modal.style.width = '100vw';
            modal.style.height = '100vh';
            modal.style.resize = 'none';
            btn.textContent = 'Restore';
        } else {
            modal.style.top = modal.dataset.originalTop;
            modal.style.left = modal.dataset.originalLeft;
            modal.style.width = modal.dataset.originalWidth;
            modal.style.height = modal.dataset.originalHeight;
            modal.style.resize = 'both';
            btn.textContent = 'Maximize';
        }
        setIsMaximized(!isMaximized);
    };

    // Make modal draggable
    useEffect(() => {
        const modal = previewModalRef.current;
        if (!modal) return;
        const header = modal.querySelector('#modalHeader');
        let isDragging = false;
        let offsetX = 0, offsetY = 0;

        const onMouseDown = (e) => {
            isDragging = true;
            offsetX = e.clientX - modal.offsetLeft;
            offsetY = e.clientY - modal.offsetTop;
            document.body.style.userSelect = 'none';
        };
        const onMouseUp = () => {
            isDragging = false;
            document.body.style.userSelect = 'auto';
        };
        const onMouseMove = (e) => {
            if (isDragging) {
                modal.style.left = (e.clientX - offsetX) + 'px';
                modal.style.top = (e.clientY - offsetY) + 'px';
            }
        };

        if (header) {
            header.addEventListener('mousedown', onMouseDown);
            document.addEventListener('mouseup', onMouseUp);
            document.addEventListener('mousemove', onMouseMove);
        }

        return () => {
            if (header) {
                header.removeEventListener('mousedown', onMouseDown);
                document.removeEventListener('mouseup', onMouseUp);
                document.removeEventListener('mousemove', onMouseMove);
            }
        };
    }, []);


    // --- Form Submission Logic ---
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (readOnly) return;

        setError(null);
        setMessage(null);

        const formData = new FormData();

        // Append top-level fields
        formData.append('name', transporter.name.toUpperCase());
        formData.append('short_name', (transporter.short_name || '').toUpperCase());
        formData.append('phone', transporter.phone);
        formData.append('address', transporter.address.toUpperCase());
        formData.append('email', transporter.email); 


        // Append Registration Details fields
        formData.append('tin', transporter.tin.toUpperCase());
        if (transporter.tin_document instanceof File) {
            formData.append('tin_document', transporter.tin_document);
        }
        formData.append('vrn', transporter.vrn.toUpperCase());
        if (transporter.vrn_document instanceof File) {
            formData.append('vrn_document', transporter.vrn_document);
        }
        formData.append('vrn_na', transporter.vrn_na ? 'true' : 'false');
        
        // Append Bank Details as JSON string
        formData.append('bank_details', JSON.stringify(transporter.bank_details));

        // Append Attachments (assuming a simplified structure for the backend)
        // If your Django backend expects nested JSON for attachments, this part needs adjustment.
        // For FormData, each attachment property needs to be appended individually with an index.
        transporter.attachments.forEach((att, index) => {
            if (att.file instanceof File) {
                formData.append(`attachments[${index}][file]`, att.file);
                formData.append(`attachments[${index}][type]`, att.type);
                console.log(`Adding attachment ${index}: type=${att.type}, file=${att.file.name}`);
            } else if (typeof att.file === 'string' && att.file !== '') {
                // For existing files, include the URL and type for backend reference
                formData.append(`attachments[${index}][file_url]`, att.file);
                formData.append(`attachments[${index}][type]`, att.type);
                console.log(`Adding existing attachment ${index}: type=${att.type}, url=${att.file}`);
            }
            // No 'remarks' field for attachments as per current spec
        });


        // Log FormData contents for debugging
        console.log("--- FormData contents before sending (TransporterForm) ---");
        for (let pair of formData.entries()) {
            // Log file names for File objects, otherwise log value
            if (pair[1] instanceof File) {
                console.log(pair[0]+ ': File - ' + pair[1].name);
            } else {
                console.log(pair[0]+ ': ' + pair[1]);
            }
        }
        console.log("-----------------------------------------------------");

        const url = initialData ? `${API_BASE_URL}${initialData.id}/` : API_BASE_URL;
        const method = initialData ? 'PUT' : 'POST';

        try {
            const response = await fetch(url, {
                method: method,
                headers: getAuthHeaders(true),
                body: formData,
            });

            if (!response.ok) {
                const errorData = await response.json();
                const errorMessage = Object.entries(errorData).map(([key, val]) => `${key}: ${val}`).join('; ');
                setError(`Save failed: ${errorMessage}`);
                toast.error(`Save failed: ${errorMessage}`);
                return;
            }

            toast.success('Transporter saved successfully!');
            if (onClose) onClose(true);

        } catch (err) {
            setError('Failed to save Transporter. Network error or server unreachable.');
            toast.error('Failed to save Transporter. Network error or server unreachable.');
        }

    };

    const handleCancel = () => {
        onClose(false);
    };

    // Log the current mode and readOnly state directly before render for debugging
    console.log(`[RENDER] Current mode: "${mode}", readOnly state: "${readOnly}"`);
    console.log(`[RENDER] Transporter state:`, transporter);

    return (
        <div className="transporter-form-container">
            <h1>Transporter Details</h1>
            <div className="toggle-buttons">
                <button type="button" onClick={() => document.querySelectorAll('.section-content').forEach(el => el.style.display = 'block')}>Expand All</button>
                <button type="button" onClick={() => document.querySelectorAll('.section-content').forEach(el => el.style.display = 'none')}>Collapse All</button>
            </div>

            {error && <div className="form-error">{error}</div>}
            {message && <div className="form-success">{message}</div>}

            <form onSubmit={handleSubmit}>
                {/* Transporter Information Section */}
                <div className="section">
                    <h2 onClick={(e) => e.target.nextElementSibling.style.display = e.target.nextElementSibling.style.display === 'block' ? 'none' : 'block'}>Transporter Information</h2>
                    <div className="section-content" style={{ display: 'block' }}>
                        <div className="row" style={{ gridTemplateColumns: '2fr 1fr 1.5fr' }}>
                            <div>
                                <label className="required">Name</label>
                                <input type="text" name="name" value={transporter.name} onChange={handleChange} required readOnly={readOnly} />
                            </div>
                            <div>
                                <label>Short Name</label>
                                <input type="text" name="short_name" value={transporter.short_name || ''} onChange={handleChange} readOnly={readOnly} />
                            </div>
                            <div>
                                <label className="required">Phone</label>
                                <input type="text" name="phone" value={transporter.phone} onChange={handleChange} readOnly={readOnly} />
                            </div>
                        </div>
                        <div className="row">
                            <div>
                                <label className="required">Address</label>
                                <div className={`address-input-container ${readOnly ? 'readonly' : ''}`}>
                                    <span className="address-prefix">P.O.BOX</span>
                                    <input 
                                        type="text" 
                                        name="address" 
                                        value={getDisplayAddress(transporter.address)}
                                        onChange={handleAddressChange}
                                        required 
                                        readOnly={readOnly}
                                        className="address-input"
                                        placeholder="Enter address details..."
                                    />
                                </div>
                            </div>
                            <div>
                                <label>Email</label>
                                <input type="text" name="email" value={transporter.email} onChange={handleChange} readOnly={readOnly} />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Registration Details Section */}
                <div className="section">
                    <h2 onClick={(e) => e.target.nextElementSibling.style.display = e.target.nextElementSibling.style.display === 'block' ? 'none' : 'block'}>Registration Details</h2>
                    <div className="section-content">
                        <div className="row">
                            <div>
                                <label>TIN</label>
                                <input type="text" name="tin" value={transporter.tin} onChange={handleChange} readOnly={readOnly} placeholder="123-456-789" />
                            </div>
                            <div>
                                <label>TIN Document</label>
                                <input type="file" name="tin_document" onChange={(e) => handleFileChange(e, 'tin_document')} disabled={readOnly} />
                                {transporter.tin_document && typeof transporter.tin_document === 'string' && (
                                    <>
                                        <span className="file-name">{transporter.tin_document.split('/').pop()}</span>
                                        <button type="button" onClick={() => openPreview(transporter.tin_document, transporter.tin_document.split('/').pop())} className="preview-btn">Preview</button>
                                    </>
                                )}
                            </div>
                            <div>
                                <label className="required">VRN</label>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <input
                                        type="text"
                                        name="vrn"
                                        value={transporter.vrn}
                                        onChange={handleChange}
                                        required={!transporter.vrn_na}
                                        readOnly={readOnly || transporter.vrn_na}
                                        pattern="\d{2}-\d{6}-[A-Z]"
                                        maxLength="11"
                                        placeholder="12-345678-A"
                                    />
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontWeight: 'normal' }}>
                                        <input
                                            type="checkbox"
                                            name="vrn_na"
                                            checked={!!transporter.vrn_na}
                                            onChange={handleChange}
                                            disabled={readOnly}
                                            style={{ marginLeft: '8px', cursor: readOnly ? 'not-allowed' : 'pointer' }}
                                        />
                                        N/A
                                    </label>
                                </div>
                            </div>
                            <div>
                                <label>VRN Document</label>
                                <input type="file" name="vrn_document" onChange={(e) => handleFileChange(e, 'vrn_document')} disabled={readOnly} />
                                {transporter.vrn_document && typeof transporter.vrn_document === 'string' && (
                                    <>
                                        <span className="file-name">{transporter.vrn_document.split('/').pop()}</span>
                                        <button type="button" onClick={() => openPreview(transporter.vrn_document, transporter.vrn_document.split('/').pop())} className="preview-btn">Preview</button>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Bank Details Section */}
                <div className="section">
                    <h2 onClick={(e) => e.target.nextElementSibling.style.display = e.target.nextElementSibling.style.display === 'block' ? 'none' : 'block'}>Bank Details</h2>
                    <div className="section-content">
                        <div className="repeatable" id="bank_details">
                            {transporter.bank_details.map((detail, index) => (
                                <div key={index} className="repeatable-row">
                                    <div style={{ flex: '2' }}>
                                        <label className="required">Bank</label>
                                        <Select2Dropdown
                                            options={banks.map(b => ({ id: b.id, text: b.name }))}
                                            value={detail.bank}
                                            onChange={val => handleRepeatedChange({ target: { value: val } }, index, 'bank', 'bank_details')}
                                            valueKey="id"
                                            displayKey="text"
                                            placeholder="Select Bank"
                                            disabled={readOnly}
                                        />
                                    </div>
                                    <div style={{ flex: '1' }}>
                                        <label>Swift Code</label>
                                        <input type="text" value={detail.swift_code || ''} readOnly placeholder="Auto-populated" />
                                    </div>
                                    <div style={{ flex: '1' }}>
                                        <label className="required">Currency</label>
                                        <Select2Dropdown
                                            options={majorCurrencies}
                                            value={detail.currency}
                                            onChange={val => handleRepeatedChange({ target: { value: val } }, index, 'currency', 'bank_details')}
                                            valueKey="id"
                                            displayKey="text"
                                            placeholder="Select Currency"
                                            disabled={readOnly}
                                        />
                                    </div>
                                    <div style={{ flex: '2' }}>
                                        <label className="required">Account Number</label>
                                        <input type="text" value={detail.account_number || ''} onChange={(e) => handleRepeatedChange(e, index, 'account_number', 'bank_details')} required readOnly={readOnly} />
                                    </div>
                                    {!readOnly && <button type="button" onClick={() => removeRepeatedItem('bank_details', index)} className="remove-btn">Remove</button>}
                                </div>
                            ))}
                        </div>
                        {!readOnly && <button type="button" onClick={() => addRepeatedItem('bank_details', { bank: '', swift_code: '', currency: 'TZS', account_number: '' })}>Add Bank Detail</button>}
                    </div>
                </div>

                {/* Attachments Section */}
                <div className="section">
                    <h2 onClick={(e) => e.target.nextElementSibling.style.display = e.target.nextElementSibling.style.display === 'block' ? 'none' : 'block'}>Attachments</h2>
                    <div className="section-content" style={{ display: 'block' }}>
                        <div className="repeatable" id="attachments">
                            {transporter.attachments.map((attachment, index) => (
                                <div key={index} className="repeatable-row">
                                    <div style={{ flex: '0.5', minWidth: '90px', marginRight: '8px' }}>
                                        <label className="required">Type</label>
                                        <input type="text" value={attachment.attachment_type || ''} onChange={(e) => handleRepeatedChange(e, index, 'attachment_type', 'attachments')} required readOnly={readOnly} placeholder="Enter document type" />
                                    </div>
                                    <div style={{ flex: '3' }}>
                                        <label className="required">Document Upload</label>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                            <input type="file" ref={el => attachmentRefs.current[index] = el} onChange={(e) => handleFileChange(e, 'attachments', index)} accept=".pdf,.jpg,.png,.doc,.docx" disabled={readOnly} style={{ width: '200px' }} />
                                            {(attachment.document_file || attachment.file_obj) && (
                                                <div className="file-links" style={{ whiteSpace: 'nowrap', display: 'inline' }}>
                                                    {typeof attachment.document_file === 'string' && attachment.document_file && <a href={attachment.document_file} target="_blank" rel="noopener noreferrer" className="preview-link">View Existing</a>}
                                                    {attachment.file_obj instanceof File && <span onClick={() => openPreview(URL.createObjectURL(attachment.file_obj), attachment.file_obj.name)} className="preview-link">View Uploaded</span>}
                                                    {attachment.file_obj instanceof File && <a href={URL.createObjectURL(attachment.file_obj)} download={attachment.file_obj.name}>Download</a>}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    {!readOnly && <button type="button" onClick={() => removeRepeatedItem('attachments', index)} className="remove-btn">Remove</button>}
                                </div>
                            ))}
                        </div>
                        {!readOnly && <button type="button" onClick={() => addRepeatedItem('attachments', { attachment_type: '', document_file: null, file_obj: null })}>Add Attachment</button>}
                    </div>
                </div>

                <div className="buttons">
                    {/* 3. Protect Edit button */}
                    {readOnly && hasPermission('TRANSPORTERS', 'can_edit') && (
                        <button type="button" onClick={() => setReadOnly(false)}>Edit</button>
                    )}
                    {/* 4. Protect Save button */}
                    {!readOnly && (
                        <>
                            <button type="submit">Save</button>
                            <button type="button" onClick={handleCancel} className="cancel-btn">Cancel</button>
                        </>
                    )}
                </div>
            </form>

            {/* File Preview Modal (This remains the same) */}
            <div id="previewModal" ref={previewModalRef} style={{ display: 'none', position: 'fixed', top: '50px', left: '50px', width: '80%', height: '80%', backgroundColor: 'white', border: '1px solid #ccc', borderRadius: '8px', zIndex: '9999', boxShadow: '0 0 10px rgba(0,0,0,0.5)', resize: 'both', overflow: 'auto' }}>
                <div id="modalHeader" style={{ cursor: 'move', background: '#f1f1f1', padding: '10px', fontWeight: 'bold', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>File Preview</span>
                    <div>
                        <button type="button" id="maximizeBtn" onClick={toggleMaximize} style={{ fontSize: '12px', marginRight: '5px' }}>Maximize</button>
                        <button type="button" onClick={closePreview} style={{ fontSize: '12px' }}>Close</button>
                    </div>
                </div>
                <iframe id="previewFrame" ref={previewIframeRef} style={{ width: '100%', height: 'calc(100% - 40px)', border: 'none', display: 'none' }} title="Content Panel"></iframe>
                <img id="previewImage" ref={previewImageRef} style={{ maxWidth: '100%', maxHeight: 'calc(100% - 40px)', display: 'none', margin: '0 auto' }} alt="Preview" />
                <div id="downloadDiv" ref={previewDownloadDivRef} style={{ padding: '20px', textAlign: 'center', display: 'none' }}></div>
            </div>

        </div>
    );
};

const majorCurrencies = [  
        { id: 'EUR', text: 'EUR - Euro' },
        { id: 'GBP', text: 'GBP - British Pound Sterling' },
        { id: 'TZS', text: 'TZS - Tanzanian Shilling' },
        { id: 'USD', text: 'USD - United States Dollar' },
    ];

export default TransporterForm;
